import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { ClientPanel } from 'pages/ClientPanel/ClientPanel';
import { apiCall, ApiResps } from 'providers';
import { AdminLayout } from '../AdminLayout';
import { withTransaction } from '@elastic/apm-rum-react';
import { generateNameFormatted } from 'helpers/titleHelper';
import { t } from 'i18next';
import { ClientHeader } from './components/ClientHeader';
import { Image } from 'antd';
import { useForm } from 'react-hook-form';
import { FaRegTrashAlt, FaFileUpload } from 'react-icons/fa';
import {
  useHistory, useRouteMatch,
} from 'react-router-dom';
import { toast } from 'react-toastify';
import { Flex, Box } from 'reflexbox';
import {
  Button, Loader, ModalWindow, ToggleSwitch,
} from 'components';
import { Input } from 'components/NewInputs/Default';
import { AddNewClientParams } from 'metadata/AddNewClient.model';
import { EditClientParams } from 'metadata/EditClient.model';
import { colors } from 'styles/colors';
import {
  Form, Card, PictureBox, ToggleContainer,
} from './styles';
import { MaskCnpj, MaskPhone } from '~/helpers/maskInputs';
import { ClientType } from './components/Form/types';
import { Select } from '~/components/NewSelect';
import { getUserProfile } from '~/helpers/userProfile';

const FORM_VALIDATORS = {
  name: {
    required: t('campoNomeNecessario'),
  },
  email: {
    required: t('campoEmailNecessario'),
  },
  phone: {
    required: false,
    validate: (value) => {
      if (value) {
        const regex = new RegExp(/^\([0-9]{2}\) [0-9]?[0-9]{4}-[0-9]{4}$/);
        return regex.test(value);
      } return true;
    },
  },
  cnpj: {
    required: false,
    validate: (value) => {
      if (value) {
        const regex = new RegExp(/^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$/);
        return regex.test(value);
      } return true;
    },
  },
};

type Inputs = {
  email: string,
  name: string,
  phone: string,
  cnpj: string,
};

const CLIENT_TYPE_OPTIONS = [
  { label: t('cliente'), value: 'cliente' },
  { label: t('mantenedor'), value: 'mantenedor' },
  { label: t('fabricante'), value: 'fabricante' },
  { label: t('parceira'), value: 'parceira' },
  { label: t('subCliente'), value: 'sub-cliente' },
];

export const EditClient = (): JSX.Element => {
  const history = useHistory();
  const match = useRouteMatch<{ clientId: string }>();
  const fabricante = match.url.includes('fabricantes');
  const clientId = match.params.clientId != null ? Number(match.params.clientId) : null;
  const [loading, setLoading] = useState(false);
  const [picture, setPicture] = useState<string | ArrayBuffer | null>(null);
  const [clientType, setClientType] = useState<ClientType>(fabricante ? 'fabricante' : 'cliente');
  const [enabled, setEnabled] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [listClient, setListClient] = useState<ApiResps['/clients/get-clients-list']['list']>([]);
  const [clientOwner, setClientOwner] = useState<{
    CLIENT_ID: number;
    NAME: string;
    PERMS_C: string;
    EMAIL?: string;
    PICTURE?: string;
    ENABLED?: string;
    clientType: ClientType[];
    CNPJ?: string;
    PHONE?: string;
    CITIES?: string[];
    STATES?: number[];
    CLIENT_OWNER?: number;
    CLIENT_OWNER_NAME?: string;
  } | null>(null);
  const [profile] = useState(getUserProfile);
  const [hideDutQaTemp, setHideDutQaTemp] = useState(false);
  const [showSimulatedProfileModal, setShowSimulatedProfileModal] = useState(false);
  const pendingSubmitRef = useRef<Inputs | null>(null);
  const [clientInfo, setClientInfo] = useState<{
    CLIENT_ID: number;
    NAME: string;
    EMAIL: string;
    PICTURE: string;
    ENABLED: string;
    PERMS_C: string;
    clientType: ClientType[];
    CNPJ: string;
    PHONE: string;
    HIDE_DUT_QA_TEMP: boolean;
  }>();

  const {
    register, setValue, handleSubmit, watch, formState: { errors },
  } = useForm<Inputs>({
    mode: 'all',
  });

  const executeSubmit = async ({ email, name }: Inputs) => {
    try {
      setLoading(true);

      const defaultParams = {
        NAME: name,
        EMAIL: email,
        PICTURE: (picture && String(picture)) || null,
        clientType: [clientType],
        ENABLED: enabled ? '1' : '0' as '1' | '0',
        CNPJ: cnpj,
        PHONE: phoneNumber,
        CLIENT_OWNER: clientOwner,
        HIDE_DUT_QA_TEMP: hideDutQaTemp,
      };

      if (clientId != null) {
        const params: EditClientParams['reqParams'] = {
          ...defaultParams,
          CLIENT_ID: clientId,
        };

        await apiCall('/clients/edit-client', params);
        toast.success(t('alteradoComSucesso'));
      } else {
        const params: AddNewClientParams['reqParams'] = {
          ...defaultParams,
          CLIENT_OWNER: defaultParams.CLIENT_OWNER?.CLIENT_ID ? {
            value: defaultParams.CLIENT_OWNER.CLIENT_ID,
          } : undefined,
        };
        await apiCall('/clients/add-new-client', params);
        toast.success(t('adicionadoComSucesso'));
      }
      history.push(fabricante ? '/painel/fabricantes' : '/painel/clientes/listagem');
    } catch (e) {
      console.log(e);
      toast.error(t('houveErro'));
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmition = async (data: Inputs) => {
    if (clientId == null && clientType === 'sub-cliente' && !USER_PARTNER && !clientOwner) {
      toast.error(t('selecioneEmpresaMae'));
      return;
    }
    if (clientId == null && localStorage.getItem('@diel:impersonateToken')) {
      pendingSubmitRef.current = data;
      setShowSimulatedProfileModal(true);
      return;
    }
    await executeSubmit(data);
  };

  const handleConfirmSimulatedCreate = async () => {
    setShowSimulatedProfileModal(false);
    if (pendingSubmitRef.current) {
      await executeSubmit(pendingSubmitRef.current);
      pendingSubmitRef.current = null;
    }
  };

  const fetchServerData = async () => {
    if (clientId == null) return;
    setLoading(true);
    try {
      const reqParams = { CLIENT_ID: clientId };
      const { client } = await apiCall('/clients/get-client-info', reqParams);
      setClientInfo(client);

      let auxClientType: ClientType | null = null;
      if ((client.clientType || []).includes('cliente')) auxClientType = 'cliente';
      if ((client.clientType || []).includes('mantenedor')) auxClientType = 'mantenedor';
      if ((client.clientType || []).includes('fabricante')) auxClientType = 'fabricante';
      if ((client.clientType || []).includes('parceira')) auxClientType = 'parceira';
      if ((client.clientType || []).includes('sub-cliente')) auxClientType = 'sub-cliente';

      auxClientType && setClientType(auxClientType);
      setValue('email', client.EMAIL);
      setValue('name', client.NAME);
      setValue('cnpj', client.CNPJ);
      setValue('phone', client.PHONE);
      setPicture(client.PICTURE);
      setEnabled(client.ENABLED === '1');
    } catch (err) {
      console.log(err);
      toast.error(t('houveErro'));
    }
    setLoading(false);
  };

  const getClientsPartners = async () => {
    setLoading(true);
    try {
      const { list } = await apiCall('/clients/get-clients-list', { withManagerPermission: true });
      const formattedList = list.filter((item) => item.PERMS_C?.includes('[ADMC]')).map((client) => ({
        value: client.CLIENT_ID,
        label: client.NAME,
        ...client,
      }));
      setListClient(formattedList);
    } catch (err) {
      console.log(err);
      toast.error(t('houveErro'));
    }
    setLoading(false);
  };

  const selectPictureFile = () => {
    const htmlNode: any = document.getElementById('file-input');
    if (htmlNode !== null) {
      htmlNode.onchange = () => {
        if (htmlNode.files.length === 1) {
          const reader = new FileReader();
          reader.addEventListener(
            'load',
            () => {
              setPicture(reader.result);
            },
            false,
          );
          reader.readAsDataURL(htmlNode.files[0]);
        }
      };
      htmlNode.click();
    }
  };

  const USER_PARTNER = profile.permissions.isAdminConta;

  useEffect(() => {
    fetchServerData();
    getClientsPartners();
  }, []);

  useEffect(() => {
    if (USER_PARTNER) setClientType('sub-cliente');
  }, [USER_PARTNER]);

  useEffect(() => {
    if (!USER_PARTNER || listClient.length === 0) return;
    const partnerClientId = profile.permissions.PER_CLIENT.find((x) => x.p.includes('[ADMC]'))?.clientId;
    const partnerClient = listClient.find((c) => c.CLIENT_ID === partnerClientId);
    if (partnerClient) setClientOwner(partnerClient);
  }, [listClient]);
  return (
    <>
      {showSimulatedProfileModal && (
        <ModalWindow
          borderTop
          onClickOutside={() => setShowSimulatedProfileModal(false)}
          style={{ width: 400, padding: '24px 32px' }}
        >
          <p style={{ marginBottom: 4, fontWeight: 'bold', color: colors.Blue700 }}>
            {t('perfilSimulado')}
          </p>
          <p style={{ marginBottom: 24, fontSize: 14 }}>
            {t('avisoPerfilSimuladoCriarCliente')}
          </p>
          <div style={{ display: 'flex', gap: 12 }}>
            <Button
              variant="primary"
              onClick={handleConfirmSimulatedCreate}
              disabled={loading}
            >
              {loading ? <Loader size="small" variant="secondary" /> : t('confirmar')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setShowSimulatedProfileModal(false)}
              disabled={loading}
            >
              {t('botaoCancelar')}
            </Button>
          </div>
        </ModalWindow>
      )}
      <Helmet>
        <title>{generateNameFormatted(clientInfo?.NAME, t('painel'))}</title>
      </Helmet>
      <AdminLayout />
      {clientId && !fabricante
        ? (
          <>
            <ClientHeader
              clientInfo={clientInfo}
              loading={loading}
            />
            <div style={{ marginTop: '16px' }}>
              <ClientPanel clientId={clientId} clientInfo={clientInfo} />
            </div>
          </>

        ) : (
          <Card>
            {loading
              ? <Loader />
              : (
                <Form onSubmit={handleSubmit(handleFormSubmition)}>
                  <Flex>
                    <Box width={[1, 1, 1, 400, 400]} mb={15}>
                      <Input
                        placeholder={t('digiteNome')}
                        isInputFilled={!!watch('name')}
                        label={t('nome')}
                        formLabel="name"
                        register={register}
                        validation={FORM_VALIDATORS.name}
                        error={errors.name ? errors.name.message : undefined}
                      />
                    </Box>
                  </Flex>
                  <Flex>
                    <Box width={[1, 1, 1, 400, 400]} mb={15}>
                      <Input
                        placeholder={t('digiteEmail')}
                        isInputFilled={!!watch('email')}
                        label="E-mail"
                        formLabel="email"
                        type="email"
                        register={register}
                        validation={FORM_VALIDATORS.email}
                        error={errors.email ? errors.email.message : undefined}
                      />
                    </Box>
                  </Flex>
                  <Flex>
                    <Box width={[1, 1, 1, 400, 400]} mb={15}>
                      <Input
                        label={t('telefone')}
                        placeholder="(00) 00000-0000"
                        isInputFilled={!!watch('phone')}
                        formLabel="phone"
                        register={register}
                        validation={FORM_VALIDATORS.phone}
                        error={errors.phone ? t('telefoneInvalido') : undefined}
                        handleChange={(e) => setPhoneNumber(MaskPhone(e.target.value))}
                        value={phoneNumber}
                      />
                    </Box>
                  </Flex>
                  <Flex>
                    <Box width={[1, 1, 1, 400, 400]} mb={15}>
                      <Input
                        label="CNPJ"
                        isInputFilled={!!watch('cnpj')}
                        placeholder={t('digiteCnpj')}
                        formLabel="cnpj"
                        register={register}
                        validation={FORM_VALIDATORS.cnpj}
                        handleChange={(e) => setCnpj(MaskCnpj(e.target.value))}
                        value={cnpj}
                        error={errors.cnpj ? t('cnpjInvalido') : undefined}
                      />
                    </Box>
                  </Flex>

                  <div style={{ marginBottom: 10 }}>
                    <div style={{ padding: 5, color: colors.Blue700, fontWeight: 'bold' }}>Logo:</div>
                    {picture ? (
                      <PictureBox style={{ border: '1px solid #c4c4c4' }}>
                        <Image
                          width={100}
                          height={50}
                          src={String(picture)}
                        />

                        <button
                          type="button"
                          style={{
                            border: 'none', padding: 10, marginLeft: 20, cursor: 'pointer', backgroundColor: 'transparent',
                          }}
                          onClick={() => setPicture(null)}
                        >
                          <FaRegTrashAlt color="#ED193F" />
                        </button>

                      </PictureBox>
                    ) : (
                      <PictureBox
                        style={{
                          border: '1px dashed #c4c4c4', cursor: 'pointer',
                        }}
                        role="button"
                        onClick={selectPictureFile}
                        tabIndex={0}
                      >

                        <span color="#555555">{t('selecionarImagem')}</span>
                        <FaFileUpload color="#555555" />
                      </PictureBox>
                    )}
                    <input id="file-input" type="file" name="clientLogo" style={{ display: 'none' }} accept="image/*" />
                  </div>
                  {!USER_PARTNER && (
                    <div style={{ paddingTop: '12px', maxWidth: 400 }}>
                      <Select
                        label={t('tipoCadastro')}
                        options={CLIENT_TYPE_OPTIONS}
                        propLabel="label"
                        value={CLIENT_TYPE_OPTIONS.find((opt) => opt.value === clientType) ?? null}
                        onSelect={(item) => setClientType(item.value as ClientType)}
                      />
                    </div>
                  )}
                  {
                    (clientType === 'sub-cliente' && !USER_PARTNER) && (
                      <div style={{ paddingTop: '12px', maxWidth: 400 }}>
                        <Select
                          label={t('selecioneEmpresaMae')}
                          options={listClient}
                          onSelect={(value) => setClientOwner(value)}
                          value={clientOwner}
                        />
                      </div>
                    )
                  }
                  <div style={{ padding: '20px 0px' }}>
                    <ToggleSwitch
                      label={enabled ? t('habilitado') : t('desativado')}
                      checked={enabled}
                      onClick={() => setEnabled(!enabled)}
                    />
                  </div>
                  {profile.permissions?.isAdminSistema && !fabricante && (
                    <ToggleContainer>
                      <ToggleSwitch
                        label={t('ocultarTemperaturaDutQa')}
                        checked={hideDutQaTemp}
                        onClick={() => setHideDutQaTemp((prev) => !prev)}
                      />
                    </ToggleContainer>
                  )}
                  <Button type="submit" variant="primary" style={{ marginTop: '10px', maxWidth: 400 }}>
                    {t('salvar')}
                  </Button>
                </Form>
              )}
          </Card>
        )}
    </>
  );
};

export default withTransaction('EditClient', 'component')(EditClient);
